/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-29
 */

// Add third-party dependencies.
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Add local dependencies.
import Header from '../../layout/Header';
import { ARIA_ROLES } from '../../../utils/accessibility';
import { formatCurrency } from '../../../utils/currency';
import LiveRegion from '../../common/LiveRegion';
import {
  useGetCurrentSubscriptionQuery,
  useGetSubscriptionUsageQuery,
  useSessionStatusQuery,
} from '../../../features/auth/authApi';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import PlanChangeDialog from '../../subscription/PlanChangeDialog';
import {
  useGetPaymentMethodsQuery,
  useGetBillingDashboardQuery,
  useGetTransactionHistoryQuery,
} from '../../../features/payment/paymentApi';
import {
  useGetInvoicesQuery,
  useCancelSubscriptionMutation,
} from '../../../features/billing/billingApi';
import AddPaymentMethodModal from './components/AddPaymentMethodModal';
import UpdateBillingAddressModal from './components/UpdateBillingAddressModal';
import { SecurePaymentProvider } from '../../payment';

/**
 * Billing page component for managing subscription, payments, and billing settings.
 * Displays real subscription data and provides beta-friendly billing experience.
 *
 * @example
 * return (
 *   <BillingPage />
 * );
 *
 * @returns {React.ReactElement} The billing page component
 */
const BillingPage: React.FC = () => {
  // Get real subscription data
  const { data: subscription, isLoading: subscriptionLoading } =
    useGetCurrentSubscriptionQuery();
  const { data: usage, isLoading: usageLoading } =
    useGetSubscriptionUsageQuery();
  const { remainingStudios } = useSubscriptionLimits();

  // Get user session data for billing email
  const { data: sessionData, isLoading: sessionLoading } =
    useSessionStatusQuery();

  // Get real payment and billing data
  const {
    data: paymentMethods,
    isLoading: paymentMethodsLoading,
    error: paymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useGetPaymentMethodsQuery();

  // Extract payment methods array from paginated response
  const paymentMethodsArray = paymentMethods?.results || [];
  const { isLoading: billingDashboardLoading, error: billingDashboardError } =
    useGetBillingDashboardQuery();
  const {
    isLoading: transactionHistoryLoading,
    error: transactionHistoryError,
  } = useGetTransactionHistoryQuery({ page: 1, page_size: 5 });

  // TODO: Use billingDashboard and transactionHistory for enhanced features
  // const { data: billingDashboard } = useGetBillingDashboardQuery();
  // const { data: transactionHistory } = useGetTransactionHistoryQuery({ page: 1, page_size: 5 });

  // Navigation
  const navigate = useNavigate();

  // Get invoices and cancellation mutation
  const { data: invoices, isLoading: invoicesLoading } = useGetInvoicesQuery(
    {}
  );
  const [cancelSubscription, { isLoading: isCancelling }] =
    useCancelSubscriptionMutation();

  // Dialog and notification state
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isAddPaymentModalOpen, setIsAddPaymentModalOpen] = useState(false);
  const [isUpdateBillingModalOpen, setIsUpdateBillingModalOpen] =
    useState(false);
  const [isInvoicesDialogOpen, setIsInvoicesDialogOpen] = useState(false);
  const [isTaxDocumentsDialogOpen, setIsTaxDocumentsDialogOpen] =
    useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);

  // Calculate loading state
  const isLoading = subscriptionLoading || usageLoading;
  const isPaymentDataLoading =
    paymentMethodsLoading ||
    billingDashboardLoading ||
    transactionHistoryLoading;

  // Get the first active payment method (simplified single payment method approach)
  const activePaymentMethod =
    paymentMethodsArray.find(pm => pm.is_active) || paymentMethodsArray[0];

  // Handle plan change success
  const handlePlanChangeSuccess = () => {
    setIsPlanDialogOpen(false);
    setShowSuccessMessage(true);
  };

  // Button handlers
  const handleViewAllInvoices = () => {
    setIsInvoicesDialogOpen(true);
  };

  const handleChangeEmail = () => {
    navigate('/profile?mode=edit');
  };

  const handleUpdateBillingAddress = () => {
    setIsUpdateBillingModalOpen(true);
  };

  const handleUpdateCard = () => {
    // For now, open the add payment method modal for updating
    // In a more complete implementation, this could open a dedicated update modal
    setIsAddPaymentModalOpen(true);
  };

  const handleDownloadTaxDocuments = () => {
    setIsTaxDocumentsDialogOpen(true);
  };

  const handleCancelSubscription = () => {
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancelSubscription = async () => {
    try {
      await cancelSubscription({}).unwrap();
      setIsCancelDialogOpen(false);
      setShowSuccessMessage(true);
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      // Handle error - could show error message
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh' }}>
      <Header />
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Live region for page load */}
        <LiveRegion
          message="Billing page loaded successfully"
          politeness="POLITE"
        />

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Billing & Subscription
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your subscription, view usage, and update payment methods
          </Typography>
        </Box>

        {/* Main Content */}
        <Box
          role={ARIA_ROLES.MAIN}
          aria-label="Billing content"
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* General Billing Dashboard Error */}
          {billingDashboardError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Billing Dashboard Error:</strong> Some billing
                information may not be available. Please refresh the page or try
                again later.
              </Typography>
            </Alert>
          )}

          {/* Current Plan Section */}
          <Card elevation={2}>
            <CardContent>
              {isLoading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>Loading subscription details...</Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                  }}
                >
                  <Box>
                    <Typography variant="h6" component="h2" gutterBottom>
                      Current Plan
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h4" color="primary">
                        {subscription?.plan?.name || 'Free'}
                      </Typography>
                      <Chip
                        label={(() => {
                          if (subscription?.plan?.is_beta_free) {
                            return 'Beta';
                          }
                          return 'Active';
                        })()}
                        color={(() => {
                          if (subscription?.plan?.is_beta_free) {
                            return 'info';
                          }
                          return 'success';
                        })()}
                        size="small"
                        icon={<CheckCircleIcon />}
                      />
                      {subscription?.plan?.is_beta_free && (
                        <Chip
                          label="No Charges"
                          color="secondary"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {(() => {
                        if (subscription?.plan?.is_beta_free) {
                          return '$0.00/month • Beta Period';
                        }
                        return `${formatCurrency(subscription?.plan?.price_monthly || 0)}/month • Billed monthly`;
                      })()}
                    </Typography>

                    {/* Discount Display - only show if user has a discount */}
                    {subscription?.has_discount &&
                      subscription?.discount_percentage &&
                      subscription.discount_percentage > 0 && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            bgcolor: (() => {
                              const discountPct =
                                subscription.discount_percentage || 0;
                              if (discountPct >= 100) {
                                return 'error.50';
                              }
                              if (discountPct >= 50) {
                                return 'warning.50';
                              }
                              return 'success.50';
                            })(),
                            borderRadius: 1,
                            border: 1,
                            borderColor: (() => {
                              const discountPct =
                                subscription.discount_percentage || 0;
                              if (discountPct >= 100) {
                                return 'error.200';
                              }
                              if (discountPct >= 50) {
                                return 'warning.200';
                              }
                              return 'success.200';
                            })(),
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              mb: 1,
                            }}
                          >
                            <Chip
                              label={
                                subscription.discount_percentage_display ||
                                `${subscription.discount_percentage}% off`
                              }
                              color={(() => {
                                const discountPct =
                                  subscription.discount_percentage || 0;
                                if (discountPct >= 100) {
                                  return 'error';
                                }
                                if (discountPct >= 50) {
                                  return 'warning';
                                }
                                return 'success';
                              })()}
                              size="small"
                            />
                            <Typography variant="body2" fontWeight="medium">
                              Active Discount
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                textDecoration: 'line-through',
                                color: 'text.secondary',
                              }}
                            >
                              {formatCurrency(subscription.plan.price_monthly)}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                color: (() => {
                                  const discountPct =
                                    subscription.discount_percentage || 0;
                                  if (discountPct >= 100) {
                                    return 'error.main';
                                  }
                                  if (discountPct >= 50) {
                                    return 'warning.main';
                                  }
                                  return 'success.main';
                                })(),
                              }}
                            >
                              →
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                color: (() => {
                                  const discountPct =
                                    subscription.discount_percentage || 0;
                                  if (discountPct >= 100) {
                                    return 'error.main';
                                  }
                                  if (discountPct >= 50) {
                                    return 'warning.main';
                                  }
                                  return 'success.main';
                                })(),
                              }}
                            >
                              {formatCurrency(
                                subscription.effective_monthly_price || 0
                              )}
                              /mo
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            You&apos;re saving{' '}
                            {formatCurrency(subscription.monthly_savings || 0)}
                            /month
                          </Typography>
                        </Box>
                      )}
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setIsPlanDialogOpen(true)}
                  >
                    Change Plan
                  </Button>
                </Box>
              )}

              {!isLoading && subscription?.plan && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Plan Features:
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={(() => {
                          if (subscription.plan.max_owned_studios === 0) {
                            return 'Unlimited studios';
                          }
                          const studioText =
                            subscription.plan.max_owned_studios === 1
                              ? 'studio'
                              : 'studios';
                          return `Up to ${subscription.plan.max_owned_studios} ${studioText}`;
                        })()}
                      />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText
                        primary={(() => {
                          if (subscription.plan.max_users_per_studio === 0) {
                            return 'Unlimited users per studio';
                          }
                          return `Up to ${subscription.plan.max_users_per_studio} users per studio`;
                        })()}
                      />
                    </ListItem>
                    {subscription.plan.features?.map(feature => (
                      <ListItem key={feature} disablePadding>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon color="success" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={feature} />
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </CardContent>
          </Card>

          {/* Usage Overview and Payment Method */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: 3,
            }}
          >
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUpIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    Usage Overview
                  </Typography>
                </Box>
                {isLoading ? (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Loading usage data...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Studios Used
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <Typography variant="h5">
                          {usage?.usage?.owned_studios?.count || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(() => {
                            if (subscription?.plan?.max_owned_studios === 0) {
                              return 'unlimited available';
                            }
                            return `of ${subscription?.plan?.max_owned_studios || 1} available`;
                          })()}
                        </Typography>
                      </Box>
                      {remainingStudios !== Infinity && (
                        <Typography variant="caption" color="text.secondary">
                          {remainingStudios} remaining
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Plan Status
                      </Typography>
                      <Typography
                        variant="h6"
                        color={(() => {
                          if (subscription?.plan?.is_beta_free) {
                            return 'info.main';
                          }
                          return 'success.main';
                        })()}
                      >
                        {(() => {
                          if (subscription?.plan?.is_beta_free) {
                            return 'Beta - No Charges';
                          }
                          return 'Active Subscription';
                        })()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        Monthly Rate
                      </Typography>
                      <Typography variant="h5">
                        {(() => {
                          if (subscription?.plan?.is_beta_free) {
                            return '$0.00';
                          }
                          return formatCurrency(
                            subscription?.plan?.price_monthly || 0
                          );
                        })()}
                      </Typography>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>

            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6" component="h2">
                    Payment Method
                  </Typography>
                </Box>

                {(() => {
                  if (subscription?.plan?.is_beta_free) {
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Beta Period:</strong> No payment method
                            required. All features are free during beta testing.
                          </Typography>
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          When the beta period ends, you will be able to add
                          payment methods to continue using premium features.
                        </Typography>
                      </Box>
                    );
                  }
                  if (paymentMethodsError) {
                    return (
                      <Box sx={{ mb: 2 }}>
                        <Alert severity="error" sx={{ mb: 2 }}>
                          <Typography variant="body2">
                            <strong>Error loading payment methods:</strong>{' '}
                            Unable to retrieve your payment information at this
                            time.
                          </Typography>
                        </Alert>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.location.reload()}
                        >
                          Retry
                        </Button>
                      </Box>
                    );
                  }
                  if (isPaymentDataLoading) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          Loading payment methods...
                        </Typography>
                      </Box>
                    );
                  }
                  if (activePaymentMethod) {
                    return (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1" gutterBottom>
                            {(() => {
                              if (activePaymentMethod.payment_type === 'card') {
                                return `•••• •••• •••• ${activePaymentMethod.card_last4}`;
                              }
                              return `•••• •••• •••• ${activePaymentMethod.bank_last4}`;
                            })()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {(() => {
                              if (activePaymentMethod.payment_type === 'card') {
                                const cardBrand = activePaymentMethod.card_brand
                                  ? `${activePaymentMethod.card_brand.charAt(0).toUpperCase()}${activePaymentMethod.card_brand.slice(1)}`
                                  : 'Card';
                                const expiryInfo =
                                  activePaymentMethod.card_exp_month &&
                                  activePaymentMethod.card_exp_year
                                    ? ` • Expires ${activePaymentMethod.card_exp_month.toString().padStart(2, '0')}/${activePaymentMethod.card_exp_year.toString().slice(-2)}`
                                    : '';
                                return `${cardBrand} ending in ${activePaymentMethod.card_last4}${expiryInfo}`;
                              }
                              return `${activePaymentMethod.bank_name || 'Bank Account'} ending in ${activePaymentMethod.bank_last4}`;
                            })()}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={handleUpdateCard}
                          >
                            Update Payment Method
                          </Button>
                        </Box>
                      </>
                    );
                  }
                  return (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          gutterBottom
                        >
                          No payment method on file
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Add a payment method to continue using premium
                          features.
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setIsAddPaymentModalOpen(true)}
                        >
                          Add Payment Method
                        </Button>
                      </Box>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Box>

          {/* Recent Invoices */}
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h2">
                  Billing History
                </Typography>
              </Box>

              {(() => {
                if (subscription?.plan?.is_beta_free) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        gutterBottom
                      >
                        No billing history yet
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        During the beta period, you will not be charged for any
                        services. Billing history will appear here once the beta
                        ends.
                      </Typography>
                    </Box>
                  );
                }

                if (transactionHistoryError) {
                  return (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>Unable to load billing history:</strong> There
                          was an issue retrieving your transaction history.
                        </Typography>
                      </Alert>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => window.location.reload()}
                      >
                        Retry
                      </Button>
                    </Box>
                  );
                }

                return (
                  <>
                    <List>
                      <ListItem
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <ListItemText
                          primary={`${new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} - ${subscription?.plan?.name || 'Current Plan'}`}
                          secondary={`${subscription?.plan?.is_beta_free ? 'Beta period' : 'Active subscription'}`}
                        />
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                        >
                          <Typography variant="body1" fontWeight="medium">
                            {subscription?.plan?.is_beta_free
                              ? '$0.00'
                              : formatCurrency(
                                  subscription?.plan?.price_monthly || 0
                                )}
                          </Typography>
                          <Chip
                            label={(() => {
                              if (subscription?.plan?.is_beta_free) {
                                return 'Beta';
                              }
                              return 'Active';
                            })()}
                            color={(() => {
                              if (subscription?.plan?.is_beta_free) {
                                return 'info';
                              }
                              return 'success';
                            })()}
                            size="small"
                          />
                        </Box>
                      </ListItem>
                    </List>
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={handleViewAllInvoices}
                      >
                        View All Invoices
                      </Button>
                    </Box>
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Billing Settings */}
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SettingsIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" component="h2">
                  Billing Settings
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Billing Email
                  </Typography>
                  {sessionLoading ? (
                    <Typography variant="body2" color="text.secondary">
                      Loading email...
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {sessionData?.user?.email || 'No email on file'}
                    </Typography>
                  )}
                  <Button
                    variant="text"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={handleChangeEmail}
                  >
                    Change Email
                  </Button>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Next Billing Date
                  </Typography>
                  {(() => {
                    if (isLoading) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Loading billing date...
                        </Typography>
                      );
                    }
                    if (subscription?.plan?.is_beta_free) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          N/A (Beta Period)
                        </Typography>
                      );
                    }
                    if (subscription?.current_period_end) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          {new Date(
                            subscription.current_period_end
                          ).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </Typography>
                      );
                    }
                    return (
                      <Typography variant="body2" color="text.secondary">
                        No billing date scheduled
                      </Typography>
                    );
                  })()}
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleUpdateBillingAddress}
                >
                  Update Billing Address
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleDownloadTaxDocuments}
                >
                  Download Tax Documents
                </Button>
                <Button
                  variant="text"
                  color="error"
                  onClick={handleCancelSubscription}
                >
                  Cancel Subscription
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Notice */}
          {subscription?.plan?.is_beta_free && (
            <Alert severity="info" icon={<WarningIcon />}>
              <Typography variant="body2">
                <strong>Beta Notice:</strong> You are currently in the beta
                period with full access to all features at no charge. Billing
                functionality will become active when the beta period ends.
              </Typography>
            </Alert>
          )}
        </Box>

        {/* Plan Change Dialog */}
        <SecurePaymentProvider>
          <PlanChangeDialog
            open={isPlanDialogOpen}
            onClose={() => setIsPlanDialogOpen(false)}
            onSuccess={handlePlanChangeSuccess}
          />
        </SecurePaymentProvider>

        {/* Success Notification */}
        <Snackbar
          open={showSuccessMessage}
          autoHideDuration={6000}
          onClose={() => setShowSuccessMessage(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowSuccessMessage(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            Plan changed successfully! Your new features are now available.
          </Alert>
        </Snackbar>

        {/* Add Payment Method Modal */}
        <SecurePaymentProvider>
          <AddPaymentMethodModal
            open={isAddPaymentModalOpen}
            onClose={() => setIsAddPaymentModalOpen(false)}
            onSuccess={() => {
              // Refresh payment methods data
              refetchPaymentMethods();
              setIsAddPaymentModalOpen(false);
              setShowSuccessMessage(true);
            }}
            existingPaymentMethod={activePaymentMethod}
          />
        </SecurePaymentProvider>

        {/* Update Billing Address Modal */}
        <UpdateBillingAddressModal
          open={isUpdateBillingModalOpen}
          onClose={() => setIsUpdateBillingModalOpen(false)}
          onSuccess={() => {
            // Refresh payment methods data to get updated billing address
            refetchPaymentMethods();
            setIsUpdateBillingModalOpen(false);
            setShowSuccessMessage(true);
          }}
        />

        {/* View All Invoices Dialog */}
        <Dialog
          open={isInvoicesDialogOpen}
          onClose={() => setIsInvoicesDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>All Invoices</DialogTitle>
          <DialogContent>
            {(() => {
              if (invoicesLoading) {
                return <Typography>Loading invoices...</Typography>;
              }

              if (invoices && invoices.results && invoices.results.length > 0) {
                return (
                  <List>
                    {invoices.results.map((invoice: any) => (
                      <ListItem key={invoice.id} divider>
                        <ListItemText
                          primary={`Invoice #${invoice.number || invoice.id}`}
                          secondary={`${new Date(invoice.created).toLocaleDateString()} - ${formatCurrency(invoice.amount_due || 0)}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                );
              }

              return (
                <Typography color="text.secondary">
                  No invoices available at this time.
                </Typography>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsInvoicesDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Tax Documents Dialog */}
        <Dialog
          open={isTaxDocumentsDialogOpen}
          onClose={() => setIsTaxDocumentsDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Tax Documents</DialogTitle>
          <DialogContent>
            <Typography color="text.secondary">
              No tax documents are currently available. Tax documents will be
              generated at the end of each tax year for accounts with billing
              activity.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsTaxDocumentsDialogOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancel Subscription Dialog */}
        <Dialog
          open={isCancelDialogOpen}
          onClose={() => setIsCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Cancel Subscription</DialogTitle>
          <DialogContent>
            <DialogContentText>
              {subscription?.plan?.is_beta_free
                ? 'Are you sure you want to cancel your free account? You will lose access to all features and your data will be permanently deleted.'
                : 'Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsCancelDialogOpen(false)}>
              Keep Subscription
            </Button>
            <Button
              onClick={handleConfirmCancelSubscription}
              color="error"
              variant="contained"
              disabled={isCancelling}
            >
              {isCancelling ? 'Cancelling...' : 'Cancel Subscription'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default BillingPage;
